import React from 'react';
import WarmupSetup from '../../../../components/Warmups/WarmupSetup';
import WarmupRoutineList from '../../../../components/Warmups/WarmupRoutineList';
import WarmupConsole from '../../../../components/Warmups/WarmupConsole';
import './LessonWarmups.css';

/**
 * The warmup player as the Lesson Dashboard mounts it — and, since I1 phase 1,
 * the one place that owns its loading, error and no-student states for EVERY
 * region, so those never get written twice and never disagree.
 *
 * `region` says which part of the player to render here:
 *   'full'    setup + rows + deck, as one card — below 1200px, in the notes
 *             column, exactly as before I1.
 *   'routine' setup + rows only — the desktop right column, when the deck has
 *             gone up into the conducting strip.
 *   'console' the deck alone — what ConductingStrip asks for.
 *
 * The engine and the routine state are NOT created here: `useWarmupPlayer` and
 * `useWarmupRoutinePlayer` both live in LessonDashboard/index.js, one instance
 * each per page, and arrive as `warmups.wp`. That is the whole reason phase 0
 * happened — two instances would be two "current exercise" states over one
 * sampler, and the strip and the right column are different subtrees.
 */
const LessonWarmups = ({ warmups, region = 'full' }) => {
	const { wp, routine } = warmups;

	if (routine.catalogError) {
		return <p className="ld-warmups__status ld-warmups__status--error">{routine.catalogError}</p>;
	}
	if (!routine.hasStudent) {
		return (
			<p className="ld-warmups__status">
				<i className="fa-sharp fa-solid fa-user-plus" aria-hidden="true" /> This lesson has no student yet. Link one from the student drawer to use warmups.
			</p>
		);
	}
	if (routine.loading || !routine.routine) {
		return (
			<p className="ld-warmups__status" role="status">
				<i className="fa-sharp fa-solid fa-spinner fa-spin" aria-hidden="true" /> Loading warmups…
			</p>
		);
	}

	const draft = {
		dirty: routine.dirty,
		changes: routine.changes,
		saving: routine.saving,
		error: routine.saveError,
		onSave: routine.save,
		onDiscard: routine.discard,
	};

	if (region === 'console') {
		return <WarmupConsole wp={wp} />;
	}

	if (region === 'routine') {
		return (
			<div className="ld-warmups ld-warmups--routine">
				{/* KEEP `wp wp--compact` even though the deck is not here. Those are
				    not decoration: `.wp--compact .wp__rowmain` is what collapses a row
				    to purpose / name / meta on their own lines, and `.wp` is the flex
				    column the setup block and the list sit in. Dropping the wrapper
				    because "there is no player here any more" silently un-styles every
				    row, and jsdom cannot see it. */}
				<section className="wp wp--compact" aria-label="Warm-up routine">
					{/* The transport controls the strip drops as it narrows ride INSIDE
					    the Routine settings disclosure — always-rendered row, one tap to
					    open, which is the design's placement and is not a hidden control
					    the way a hover affordance is (the installed PWA has no hover).
					    Rendering them loose above the routine was tried and read as a
					    wall of buttons between the teacher and the exercises. Not
					    `.wp__deck`: that name means "the transport", and there is still
					    exactly one of those, up in the band. */}
					<WarmupSetup
						wp={wp}
						draft={draft}
						headerLine={routine.lastLessonLine}
						compact
						extraControls={<WarmupConsole wp={wp} sections={['transport', 'keys', 'mix']} className="ld-warmups__extras" />}
					/>
					<WarmupRoutineList wp={wp} compact />
				</section>
			</div>
		);
	}

	return (
		<div className="ld-warmups">
			<section className="wp wp--compact" aria-label="Warmup player">
				<WarmupSetup wp={wp} draft={draft} headerLine={routine.lastLessonLine} compact />
				<div className="wp__body">
					<WarmupRoutineList wp={wp} compact />
					<WarmupConsole wp={wp} />
				</div>
			</section>
		</div>
	);
};

export default LessonWarmups;
